import { zodResolver } from '@hookform/resolvers/zod'
import { Briefcase, Loader2 } from 'lucide-react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FormSelect } from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { isApiError } from '@/lib/api/errors'
import { useDepartments, useSavePosition } from '../api'
import { POSITION_LEVELS, type Position } from '../types'
import { toastError } from '../ui'
import { FormDrawer } from './FormDrawer'

type Props = { open: boolean; onOpenChange: (v: boolean) => void; position: Position | null }

export function PositionFormDialog({ open, onOpenChange, position }: Props) {
  return open ? <PositionForm key={position?.id ?? 'new'} position={position} onDone={() => onOpenChange(false)} /> : null
}

function PositionForm({ position, onDone }: { position: Position | null; onDone: () => void }) {
  const { t } = useTranslation()
  const save = useSavePosition()
  const departments = useDepartments().data ?? []

  const schema = z.object({
    title: z.string().trim().min(2, t('access.positions.form.titleRequired')).max(80),
    description: z.string().trim().max(300),
    level: z.enum(POSITION_LEVELS),
    departmentId: z.string().min(1, t('access.positions.form.departmentRequired')),
  })

  const form = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: position?.title ?? '',
      description: position?.description ?? '',
      level: position?.level ?? 'MID',
      departmentId: position?.departmentId ?? '',
    },
  })
  const { errors } = form.formState

  const submit = async (v: z.infer<typeof schema>, confirmDepartmentChange = false) => {
    try {
      await save.mutateAsync({
        id: position?.id,
        input: {
          title: v.title,
          description: v.description,
          level: v.level,
          departmentId: v.departmentId,
          confirmDepartmentChange,
        },
      })
      toast.success(t(position ? 'access.positions.updated' : 'access.positions.created'))
      onDone()
    } catch (e) {
      if (isApiError(e) && e.status === 409 && position && !confirmDepartmentChange) {
        const ok = window.confirm(e.message || t('access.positions.form.confirmDepartmentChange'))
        if (ok) await submit(v, true)
        return
      }
      toastError(e)
    }
  }

  const onSubmit = form.handleSubmit((v) => void submit(v))

  return (
    <FormDrawer
      open
      onOpenChange={(v) => !v && onDone()}
      dismissible={!save.isPending}
      size="md"
      icon={<Briefcase />}
      title={t(position ? 'access.positions.form.editTitle' : 'access.positions.form.createTitle')}
      description={t('access.positions.form.hint')}
      onSubmit={(e) => void onSubmit(e)}
      footer={
        <>
          <Button type="button" variant="cancel" onClick={onDone}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" disabled={save.isPending}>
            {save.isPending ? <Loader2 className="animate-spin" /> : null}
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="pos-title">{t('access.positions.form.title')}</Label>
          <Input id="pos-title" aria-invalid={!!errors.title} {...form.register('title')} />
          {errors.title ? <p className="text-xs text-destructive">{errors.title.message}</p> : null}
          {position?.code ? (
            <p className="text-xs text-muted-foreground">
              {t('access.positions.form.code')}: <span className="font-mono">{position.code}</span>
            </p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label htmlFor="pos-desc">{t('access.positions.form.description')}</Label>
          <Textarea id="pos-desc" rows={2} maxLength={300} {...form.register('description')} />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-2">
            <Label htmlFor="pos-department">{t('access.positions.form.department')}</Label>
            <FormSelect
              id="pos-department"
              control={form.control}
              name="departmentId"
              options={departments.map((d) => ({ value: d.id, label: d.name, description: d.code }))}
              placeholder={t('access.positions.form.departmentPick')}
            />
            {errors.departmentId ? <p className="text-xs text-destructive">{errors.departmentId.message}</p> : null}
          </div>
          <div className="space-y-2">
            <Label htmlFor="pos-level">{t('access.positions.form.level')}</Label>
            <FormSelect
              id="pos-level"
              control={form.control}
              name="level"
              options={POSITION_LEVELS.map((level) => ({ value: level, label: t(`access.positions.levels.${level}`) }))}
            />
          </div>
        </div>
      </div>
    </FormDrawer>
  )
}
